import { Box, Typography } from "@mui/material";
import { memo } from "react";
import { WideOrderBookRow } from "./hooks";
import { find as findGrid } from "./allGrids";

import "./spotCell.css";

type Props = {
  row: WideOrderBookRow;
  clusterId: string;
};

/**
 * Day-ahead spot price for one cluster and one delivery window.
 *
 * Always one figure per member area, in the cluster's own area order, whether or
 * not they cleared at the same price — coupled areas usually agree, but a trader
 * reading a collapsed single number cannot tell agreement from a cluster that
 * simply has one number to show. The divider between figures turns orange when
 * they diverge, which is what decoupling looks like. Members without a cleared
 * price hold their slot as a dash, so position keeps meaning area.
 *
 * Deliberately not a button, unlike {@link QuoteCell}: spot is a cleared
 * historical reference, not a tradeable side, so clicking it must not prefill an
 * order. It is also rendered in a neutral colour rather than the bid/ask
 * green/red, so it reads as context rather than as a live quote.
 */
const SpotCellComponent = ({ row, clusterId }: Props) => {
  const cell = row.cells[clusterId];

  // `cell.spot` is the first member that cleared, so undefined means no member
  // in this cluster has a price at all — nothing to lay out.
  if (cell === undefined || cell.spot === undefined) {
    return (
      <Box component="span" sx={{ color: "text.disabled" }}>
        ·
      </Box>
    );
  }

  return (
    <Box
      className={
        cell.spotDecoupled ? "spot-cell spot-cell--decoupled" : "spot-cell"
      }
    >
      {cell.members.map((member) => (
        <Typography
          key={member.areaCode}
          title={
            member.openForTrading
              ? findGrid(member.areaCode).displayName
              : `${findGrid(member.areaCode).displayName} (not open for trading)`
          }
          flex="1"
          variant="body2"
          // Centred in every case, not just for a lone member: the divider in
          // spotCell.css sits on the slot boundary, which is only the true
          // midpoint between two figures while both are centred in their slot.
          textAlign="center"
          color={member.spot === undefined ? "text.disabled" : "text.secondary"}
          // A suspended area keeps its slot rather than being dropped, or the
          // remaining figures would shift and position would stop meaning area.
          // Dimming is what says it is no longer tradeable.
          sx={member.openForTrading ? undefined : { opacity: 0.45 }}
        >
          {member.spot?.yenPerKwh.toFixed(2)}
        </Typography>
      ))}
    </Box>
  );
};

export const SpotCell = memo(SpotCellComponent);

SpotCell.displayName = "SpotCell";
